import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ConsultCTA } from "../components/ConsultCTA";
import { Icon } from "../components/Icon";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { Timeline } from "../components/Timeline";
import { SERVICES, WORK_AREAS } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const TINTS = {
    teal: "bg-teal-soft text-teal-dark",
    sky: "bg-sky/15 text-sky",
    peach: "bg-peach-soft text-peach-deep",
};

const Services = () => {
    usePageMeta(
        "Speech Therapy Services in Patna | Speech Horizon — Anishabad",
        "Speech delay therapy, language development, articulation, stammering & fluency, voice therapy and autism communication therapy in Anishabad, Patna at Speech Horizon."
    );

    return (
        <div data-testid="services-page">
            <PageHero
                eyebrow="Our Services"
                lines={["Speech Therapy"]}
                highlight="Services"
                description="Personalized and goal-oriented therapy for children and adults — supporting speech, language, communication, fluency, and voice."
            />

            <section className="bg-cream py-20 md:py-28" data-testid="services-list-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((service, i) => (
                            <Reveal key={service.title} delay={i * 0.07}>
                                <div
                                    className="group flex h-full flex-col rounded-[1.75rem] border border-navy/5 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-teal/25 hover:shadow-2xl hover:shadow-teal/10"
                                    data-testid={`services-card-${service.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    <span
                                        className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 ${TINTS[service.tint]}`}
                                    >
                                        <Icon name={service.icon} size={24} strokeWidth={1.9} />
                                    </span>
                                    <h2 className="mt-6 font-jakarta text-xl font-bold text-navy">{service.title}</h2>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/65">
                                        {service.description}
                                    </p>
                                    <Link
                                        to={service.link}
                                        data-testid={`services-learn-more-${service.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark transition-colors hover:text-peach-deep"
                                    >
                                        Learn More
                                        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="services-work-areas-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="Areas of Focus"
                        title="What We Work On"
                        description="Therapy supports the whole picture of communication — from first words to confident conversation."
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {WORK_AREAS.map((area, i) => (
                            <Reveal key={area.title} delay={i * 0.06}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-sky/10">
                                    <div className="flex items-center gap-4">
                                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky/10 text-sky transition-transform duration-500 group-hover:scale-110">
                                            <Icon name={area.icon} size={21} strokeWidth={1.9} />
                                        </span>
                                        <h3 className="font-jakarta text-lg font-bold text-navy">{area.title}</h3>
                                    </div>
                                    <p className="mt-4 text-sm leading-relaxed text-ink/65">{area.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="services-timeline-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="The Process"
                        title="How Therapy Works"
                        description="A clear, structured journey — from first assessment to confident everyday communication."
                    />
                    <Timeline />
                </div>
            </section>

            <ConsultCTA />
        </div>
    );
};

export default Services;
